import { Button, Select } from '@pyxis/react';
import React, { useContext } from 'react';

import { SpaceType } from '../../components/module/SpacingOptions';
import { Text } from '../../components/module/Text';
import { Title } from '../../components/module/Title';
import { Wrapper } from '../../components/module/Wrapper';
import { PageContext } from '../../context/PageContext';
import { formatDates } from '../../helpers/utils/date';
import { truncate } from '../../helpers/utils/string';
import { CountryType, TagType } from '../../types';
import ComposableCard, { ComposableCardProps } from '../CardGrid/ComposableCard';
import { ArticleFeedFeedType } from './ArticleFeedOptions';

export type ArticleFeedProps = {
  theme?: {
    space?: SpaceType;
  };
  feedType?: ArticleFeedFeedType;
  items?: (ComposableCardProps & {
    _key?: string;
    date?: string | [string, string];
    countries?: CountryType[];
    tags?: TagType[];
  })[];
};

const PAGE_SIZE = 9;

export const ArticleFeed = ({ theme, feedType, items }: ArticleFeedProps) => {
  // console.log('feed type', feedType);

  const { config, locale } = useContext(PageContext);
  const translations = config.translations;

  const [page, setPage] = React.useState(1);
  const [nextActiveTag, setNextActiveTag] = React.useState<string>(null);
  const [nextActiveCountry, setNextActiveCountry] = React.useState<string>(null);

  const [activeTag, setActiveTag] = React.useState<string>(null);
  const [activeCountry, setActiveCountry] = React.useState<string>(null);

  const hasActiveFilters = [
    activeTag,
    nextActiveTag,
    activeCountry,
    nextActiveCountry,
  ].filter(Boolean).length;

  items = items?.map((item) => ({
    ...item,
    type: 'card.composable',
  }));

  // filter by tag
  let filteredItems = activeTag
    ? items?.filter(({ tags }) => tags?.some(({ slug }) => slug === activeTag))
    : items;

  // filter by country
  filteredItems = activeCountry
    ? filteredItems?.filter(({ countries }) =>
        countries?.some(({ slug }) => slug === activeCountry),
      )
    : filteredItems;

  // paginate items
  const paginatedItems = filteredItems?.slice(0, PAGE_SIZE * page);

  // show load more button
  let hasMore = filteredItems?.length > paginatedItems?.length;

  // handle load more
  const onLoadMoreClick = () => {
    setPage((page) => ++page);
  };

  // generate set of unique used tags
  const availableTags = React.useMemo(() => {
    return getUniqueTags(items, 'tags') as TagType[];
  }, [items]);

  const availableCountries = React.useMemo(() => {
    return getUniqueTags(items, 'countries') as CountryType[];
  }, [items]);

  const onSubmitClick = React.useCallback(() => {
    setPage(1);
    setActiveCountry(nextActiveCountry);
    setActiveTag(nextActiveTag);
  }, [nextActiveTag, nextActiveCountry]);

  const onResetClick = React.useCallback(() => {
    setPage(1);
    setNextActiveCountry(null);
    setNextActiveTag(null);
    setActiveCountry(null);
    setActiveTag(null);
  }, []);

  console.log('article items ', items);

  return (
    <>
      <Wrapper theme={theme}>
        {/* filters */}
        {(Boolean(availableCountries?.length) || Boolean(availableTags?.length)) && (
          <div className="flex flex-wrap gap-y-4 gap-x-10 items-end pb-20 relative z-20">
            {Boolean(availableCountries?.length) && (
              <div className="flex-1 min-w-40">
                {/* country */}
                <Select
                  items={[
                    {
                      value: null,
                      label:
                        translations?.article_feed_select_all_countries?.[locale] ||
                        '…',
                    },
                    ...availableCountries.map(({ title, slug }) => ({
                      value: slug,
                      label: title,
                    })),
                  ]}
                  name="country"
                  onChange={(e, value) => {
                    setPage(1);
                    setNextActiveCountry(value);
                  }}
                  value={nextActiveCountry}
                  label={
                    translations?.article_feed_countries_label?.[locale] || 'Country'
                  }
                  size="m"
                  className="[&_input]:rounded-[0.5em]"
                />
              </div>
            )}
            {/* tag */}
            {Boolean(availableTags?.length) && (
              <div className="flex-1 min-w-40">
                <Select
                  items={[
                    {
                      value: null,
                      label:
                        translations?.article_feed_select_all_categories?.[locale] ||
                        '…',
                    },
                    ...availableTags.map(({ title, slug }) => ({
                      value: slug,
                      label: title,
                    })),
                  ]}
                  name="category"
                  onChange={(e, value) => {
                    setPage(1);
                    setNextActiveTag(value);
                  }}
                  value={nextActiveTag}
                  label={
                    translations?.article_feed_categories_label?.[locale] ||
                    'Category'
                  }
                  size="m"
                  className="[&_input]:rounded-[0.5em]"
                />
              </div>
            )}
            <div className="flex no-wrap gap-2 pb-0.5">
              <Button
                variant="primary"
                size="l"
                onClick={onSubmitClick}
                disabled={
                  activeCountry === nextActiveCountry && activeTag === nextActiveTag
                }
              >
                {translations?.article_feed_search_label?.[locale] || 'Search'}
              </Button>
              <Button
                variant="ghost"
                size="l"
                onClick={onResetClick}
                disabled={!hasActiveFilters}
              >
                {translations?.article_feed_reset_label?.[locale] || 'Reset'}
              </Button>
            </div>
          </div>
        )}

        {/* grid */}

        {paginatedItems?.filter(Boolean).length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 !gap-10">
            {/* show paginated list of item  */}
            {paginatedItems?.map((item) => {
              // console.log(formatDates(item));
              return (
                <div key={item._key} className="w-full">
                  <ComposableCard
                    {...item}
                    badge={item.date as string}
                    themeName={feedType}
                    text={truncate(item.text || '', 120)}
                  />
                </div>
              );
            })}
          </div>
        ) : // if has filters, show no results message
        activeCountry || activeTag ? (
          <div className="flex flex-col gap-3 text-neutral-25">
            <Title size="3xl" color="neutral-25">
              {translations?.article_feed_no_results_title?.[locale] ||
                'No results found'}
            </Title>
            <Text size="xl">
              {translations?.article_feed_no_results_text?.[locale] ||
                'Please try an alternative combination of filters.'}
            </Text>
          </div>
        ) : // no filters and no results, show nothing
        null}

        {hasMore && (
          <div className="flex justify-center pt-20">
            <Button variant="primary" size="l" onClick={onLoadMoreClick}>
              {translations?.article_feed_load_more_label?.[locale] || 'Load more'}
            </Button>
          </div>
        )}
      </Wrapper>
    </>
  );
};

export default React.memo(ArticleFeed);

const getUniqueTags = (
  items: {
    tags?: TagType[];
    countries?: CountryType[];
  }[],
  key,
) => {
  if (!key) return items;
  const usedTags: Record<string, string> = {};
  const tags = items?.reduce((acc, item) => {
    if (item[key]) {
      item[key].forEach((tag) => {
        if (!usedTags[tag.slug]) {
          usedTags[tag.slug] = tag.slug;
          acc.push(tag);
        }
      });
    }
    return acc;
  }, [] as TagType[]);
  return tags?.sort((a, b) => a.title.localeCompare(b.title));
};
